import { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { searchAssignableUsers } from '../../api/projectMembers';
import { Project, ProjectMember } from './types';
import { MemberBuilder } from './MemberBuilder';
import { SELF_MEMBER_ID } from './memberLabels';

const useStyles = makeStyles((theme: Theme) => ({
  dialogField: {
    marginTop: theme.spacing(2),
  },
}));

type CreateProjectDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreateProject: (input: {
    name: string;
    description: string;
  }) => Promise<Project>;
  onUpdateProject: (
    projectId: string,
    updates: Partial<Project>,
  ) => Promise<void>;
  onAssignMembers: (
    projectId: string,
    members: ProjectMember[],
  ) => Promise<ProjectMember[]>;
};

function createInitialMembers(selfName: string): ProjectMember[] {
  return [
    {
      id: SELF_MEMBER_ID,
      name: selfName,
      email: '',
      role: 'Owner',
    },
  ];
}

export function CreateProjectDialog(props: CreateProjectDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unassignedMembers, setUnassignedMembers] = useState<ProjectMember[]>(
    [],
  );
  const [draftMembers, setDraftMembers] = useState<ProjectMember[]>(() =>
    createInitialMembers(t('members.selfName')),
  );

  // Created the first time a draft member needs an org-membership check, so
  // that check can run against a real project instead of waiting for the
  // final "Create" click (SearchAssignableUsers is permission-gated per
  // project, so there's no way to call it before a project exists). A ref,
  // not state, so two checks started before the first resolves share the
  // same in-flight creation instead of each creating their own project.
  const pendingProjectRef = useRef<Promise<Project> | undefined>(undefined);

  const ensureProjectCreated = (): Promise<Project> | undefined => {
    if (pendingProjectRef.current) return pendingProjectRef.current;
    const trimmedName = name.trim();
    if (!trimmedName) return undefined;
    const pending = props.onCreateProject({
      name: trimmedName,
      description: description.trim(),
    });
    pendingProjectRef.current = pending;
    pending.catch(() => {
      // Let the next check retry instead of being stuck on a failed create.
      pendingProjectRef.current = undefined;
    });
    return pending;
  };

  const checkOrgMembership = async (email: string) => {
    const pending = ensureProjectCreated();
    if (!pending) return undefined;
    try {
      const project = await pending;
      const results = await searchAssignableUsers(project.id, email);
      if (results.length === 0) {
        // No one in the org has this email or a name containing it -
        // confidently not a member.
        return false;
      }
      const exactMatch = results.some(
        user => user.email.toLowerCase() === email.toLowerCase(),
      );
      // A non-empty result with no exact email match is inconclusive, not
      // a confirmed non-member: the real match could be in there with a
      // blank/different email on record, or past the result limit. Stay
      // quiet rather than risk a false "not in org" flag.
      return exactMatch ? true : undefined;
    } catch {
      return undefined;
    }
  };

  const reset = () => {
    setName('');
    setDescription('');
    setSubmitting(false);
    setUnassignedMembers([]);
    setDraftMembers(createInitialMembers(t('members.selfName')));
    pendingProjectRef.current = undefined;
  };

  const closeDialog = () => {
    reset();
    props.onClose();
  };

  const createProject = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }

    setSubmitting(true);
    try {
      const trimmedDescription = description.trim();
      const project = await (pendingProjectRef.current ??
        props.onCreateProject({
          name: trimmedName,
          description: trimmedDescription,
        }));
      if (
        project.name !== trimmedName ||
        project.description !== trimmedDescription
      ) {
        await props.onUpdateProject(project.id, {
          name: trimmedName,
          description: trimmedDescription,
        });
      }
      const failedMembers = await props.onAssignMembers(
        project.id,
        draftMembers,
      );
      if (failedMembers.length) {
        setUnassignedMembers(failedMembers);
        return;
      }
      closeDialog();
    } finally {
      setSubmitting(false);
    }
  };

  // The project exists once onCreateProject has returned, so what is left to
  // show is the result. Showing the form again would invite a second,
  // duplicate project.
  const createdWithFailures = unassignedMembers.length > 0;

  return (
    <Dialog open={props.open} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>
        {createdWithFailures
          ? t('createDialog.createdTitle')
          : t('createDialog.title')}
      </DialogTitle>
      <DialogContent>
        {createdWithFailures ? (
          <Alert severity="warning">
            {t('createDialog.membersNotAdded', {
              emails: unassignedMembers.map(member => member.email).join(', '),
            })}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary">
              {t('createDialog.description')}
            </Typography>
            <TextField
              className={classes.dialogField}
              fullWidth
              label={t('fields.projectName')}
              value={name}
              onChange={event => setName(event.target.value)}
              variant="outlined"
            />
            <TextField
              className={classes.dialogField}
              fullWidth
              label={t('fields.description')}
              value={description}
              onChange={event => setDescription(event.target.value)}
              variant="outlined"
              multiline
              minRows={3}
            />
            <MemberBuilder
              members={draftMembers}
              onMembersChange={setDraftMembers}
              checkOrgMembership={checkOrgMembership}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>
          {createdWithFailures ? t('actions.close') : t('actions.cancel')}
        </Button>
        {!createdWithFailures && (
          <Button
            color="primary"
            variant="contained"
            disabled={!name.trim() || submitting}
            onClick={createProject}
          >
            {t('actions.create')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
